import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { createUseStyles } from 'react-jss'
import { useAtom } from 'jotai'
import moment from 'moment'
import { Department } from '../../../models/Department'
import { mapMembers } from '../../../models/Member'
import { Button } from '../../ui/Button/Button'
import { FormInputNumber } from '../../modules/FormElements/FormInput'
import { searchMembers } from '../../../api/members/members'
import {
    searchMembersAtom,
    searchPageAtom,
} from '../../../store/filter/filterAtom'

import { getInitials } from '../../../utils/initials'
import { Badge } from '../../ui/Badge/Badge'
import {
    Card,
    CardCell,
    CardList,
    CardMobile,
    CardHeaderCell,
    CardHeader,
    CardContentMobile,
    CircleText,
    Circle,
} from '../../ui/Card'
import { Typography } from '../../ui/Typography/Typography'
import breakpoints from '../../../theme/breakpoints'
import { colors } from '../../../theme/palette'
import { PersonSearch } from '../../../assets/icons'
import { getDepartments } from '../../../api/departments'
import { ISelectOptions, Select } from '../../ui/FormElememts/Select'
import { CardWithIconAndMessge } from '../../ui/Card/CardWithIconAndMessge'
import { SortButton } from '../../ui/SortButton'
import useSortMembersList from '../../../hooks/useSortMembersList'
import { DatePickerComponent } from '../../ui/FormElememts/DatePicker'
import { Loader } from '../../ui/Loaders/Loader'
import { FormMessage } from '../../ui/FormElememts/FormMessage'
import { L, L_HTML } from '../../providers/Lokalise'
import { HeaderLinks } from '../../modules/Headerlinks'
import Input from '../../ui/FormElememts/Input'
import { Spinner } from '../../ui/Loaders/Spinner'

export type RegistrationFormFields = {
    fuldeNavn: string
    foedselsesdato: string
    telefon: string
    afdelingsnummer: string
}

const useStyles = createUseStyles({
    container: {
        padding: '0 24px',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                padding: 0,
            },
        },
    },
    headerArea: {
        display: 'flex',
        padding: '.5rem 0',
        minHeight: '80px',
    },
    topArea: {
        borderBottom: `1px solid ${colors.gray700}`,
        // TODO: remove this when this area has been developed.
        minHeight: '60px',
    },
    headerActions: {
        marginLeft: 'auto',
        flexShrink: 0,
    },
    members: {
        paddingTop: '20px',
    },
    form: {
        display: 'grid',
        gap: '10px',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                gridTemplateColumns: '1fr 180px 140px 1fr 140px',
            },
        },
    },
    linkButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0',
        margin: '0',
        borderRadius: '6px',
        boxSizing: 'border-box',
        textDecoration: 'none',
        fontWeight: 400,
        textAlign: 'center',
        transition: 'all 0.2s',
        boxShadow: 0,
        background: 'none',
        '&:hover': {
            cursor: 'pointer',
        },
        '-webkit-appearance': 'none',
        '-moz-appearance': 'none',
        border: 0,
        color: colors.green500,
        fontSize: '.875rem',
        textDecorationLine: 'underline',
    },
    subArea: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '1rem 160px 1rem 1rem',
        borderBottom: `1px solid ${colors.borderColor}`,
    },
    circleIcon: {
        width: '62px',
        height: '62px',
        borderRadius: '50%',
        border: `2px solid ${colors.white}`,
        backgroundColor: colors.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '1rem',
    },
    content: {
        paddingTop: '1.875rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    messageWrapper: {
        marginTop: '2.25rem',
    },
    selectContainer: {
        position: 'relative',
    },
    selectTrigger: {
        position: 'relative',
        minHeight: '48px',
        backgroundColor: colors.white,
        border: `1px solid ${colors.borderColor}`,
        borderRadius: '6px',
        width: '100%',
        fontSize: '1.125rem',
        paddingLeft: '.75rem',
        paddingRight: '.75rem',
        cursor: 'pointer',
        color: colors.labelColor,
    },
    selectContent: {
        overflow: 'hidden',
        backgroundColor: 'white',
        borderRadius: '6px',
        boxShadow:
            // eslint-disable-next-line max-len
            '0px 10px 38px -10px rgba(22, 23, 24, 0.35), 0px 10px 20px -15px rgba(22, 23, 24, 0.2)',
    },
    select: {
        position: 'relative',
        height: '48px',
        backgroundColor: colors.white,
        border: `1px solid ${colors.borderColor}`,
        borderRadius: '6px',
        width: '100%',
        fontSize: '1.125rem',
        paddingLeft: '.75rem',
        paddingRight: '.75rem',
        cursor: 'pointer',
        color: colors.labelColor,
    },
    numberOfMembers: {
        display: 'flex',
        padding: '30px 0 90px',
        justifyContent: 'center',
        alignItems: 'center',
    },
    headerAreaTop: {
        display: 'flex',
        padding: '1rem 0 .5rem',
        borderBottom: `1px solid ${colors.borderColor}`,
        marginBottom: '1rem',
    },
})

interface IQuery {
    [key: string]: string
}

const gridTemplateColumns = '12% 22% 22% 22% 22%'

interface IError {
    hasError: boolean
    message: string
}

const AllMembers: React.FC = () => {
    const [departmentNumber, _setDepartmentNumber] = useState(undefined)
    const { register, reset, handleSubmit, setValue, formState, getValues } =
        useForm<RegistrationFormFields>()
    const classes = useStyles()
    const [options, setOptions] = useState<ISelectOptions[]>([])
    const [error, setError] = useState<IError | undefined>(undefined)
    const [selectedDate, _setSelectedDate] = useState(undefined)
    const [loading, setLoading] = useState(false)
    const [, setResult] = useAtom(searchMembersAtom)
    const [members] = useAtom(searchPageAtom)

    const { setSort, sortState } = useSortMembersList()

    const [isValid, setIsValid] = useState(false)

    const inputReference = useRef(null)

    const { isDirty } = formState

    useEffect(() => {
        setSort('name')
    }, [])

    useEffect(() => {
        let valid = false

        if (selectedDate) {
            valid = true
        }

        if (isDirty) {
            valid = true
        }

        if (departmentNumber) {
            valid = true
        }

        setIsValid(valid)
    }, [isDirty, selectedDate, departmentNumber])

    useEffect(() => {
        const getDeps = async (): Promise<boolean> => {
            const depResponse = await getDepartments()
            const opt: ISelectOptions[] = depResponse.map(
                (department: Department) => {
                    return { label: department.name, value: department.id }
                }
            )

            reset({
                fuldeNavn: '',
                foedselsesdato: '',
                telefon: '',
                afdelingsnummer: '',
            })
            setOptions(opt)

            return true
        }

        getDeps()
    }, [register])

    const searchMembersApi = async (queryString: string): Promise<void> => {
        const start = performance.now()

        const response = await searchMembers(queryString)

        if (response && response.data) {
            const result = mapMembers(response.data.Medlemmer)

            if (!result.length) {
                setError({
                    hasError: true,
                    message: 'Ingen resultater fundet',
                })
                setResult([])
                // setMembers([])
            } else {
                setResult(result)
                // setMembers(result)
                setError(undefined)
            }
        }

        const duration = performance.now() - start
        let timeout = 0

        if (duration < 500) {
            timeout = 500 - duration
        }

        setTimeout(() => {
            setLoading(false)
            if (inputReference && inputReference.current) {
                inputReference.current.focus()
            }
        }, timeout)
    }

    const onSubmit = (data: RegistrationFormFields): void => {
        setLoading(true)

        const values = { ...data }

        if (!data.afdelingsnummer) {
            delete values.afdelingsnummer
        }

        const query: IQuery = {}
        Object.keys(values).forEach((value) => {
            if (value && value !== '') {
                query[`request.${value}`] = data[value]
            }
        })
        const queryString = Object.keys(query)
            .map((key) => `${key}=${query[key]}`)
            .join('&')

        searchMembersApi(queryString)
    }

    const setSelectedDate = (date: Date | undefined | null): void => {
        _setSelectedDate(date)
        if (date) {
            setValue('foedselsesdato', date.toDateString())
        } else {
            setValue('foedselsesdato', '')
        }
    }

    const setDepartmentNumber = (value: string | undefined): void => {
        setValue('afdelingsnummer', value)
        _setDepartmentNumber(value)
    }

    const max = new Date()

    const { errors } = formState

    const validateMobile = (number: string | undefined | null): boolean => {
        if (!number || number.trim() === '') {
            return true
        }

        if (number.length > 7) {
            return true
        }

        return false
    }

    const validateName = (name: string | undefined | null): boolean => {
        if (!name || name.trim() === '') {
            return true
        }

        if (name.length > 43) {
            return true
        }

        return false
    }

    return (
        <div className={`${classes.container} page-up`}>
            <div className={`${classes.headerAreaTop} desktop-only`}>
                <div className={classes.headerActions}>
                    <HeaderLinks showGotoMembrsearchModal={false} />
                </div>
            </div>
            <div className={`${classes.headerArea}`}>
                <div className="desktop-only">
                    <Typography tag="h1" size="XXXXL">
                        {L_HTML('ALLEMEDLEMMER_TITEL')}
                    </Typography>
                </div>
                <div className="mobile-only">
                    <Typography tag="h1" size="XL" align="center">
                        {L_HTML('ALLEMEDLEMMER_TITEL')}
                    </Typography>
                </div>
            </div>
            <div
                className={`${classes.headerArea} desktop-only`}
                style={{ marginBottom: '1rem' }}
            >
                <div>
                    <div className={classes.circleIcon}>
                        <PersonSearch color={colors.green500} width="32px" />
                    </div>
                </div>
                <div>
                    <Typography
                        tag="h2"
                        size="M"
                        color={colors.black}
                        style={{ marginBottom: '1rem' }}
                    >
                        {L_HTML('ALLEMEDLEMMER_BESKRIVELSE_TITEL')}
                    </Typography>
                    <Typography tag="p" size="S" color={colors.gray900}>
                        {L_HTML('ALLEMEDLEMMER_BESKRIVELSE_TEXT')}
                    </Typography>
                </div>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
                <div>
                    <Input
                        label="Navn"
                        name="fuldeNavn"
                        required={false}
                        maxLength={43}
                        autoFocus
                        ref={inputReference}
                        {...register('fuldeNavn')}
                    />
                </div>

                <DatePickerComponent
                    label={L('GENERISK_FORM_FØDSELDAG_LABEL')}
                    selected={selectedDate}
                    setSelected={setSelectedDate}
                    maxDate={moment().subtract(1, 'days').toDate()}
                    disableCalendar
                />
                <div>
                    <FormInputNumber
                        label={L('ALLEMEDLEMMER_FORM_MOBIL_LABEL')}
                        name="telefon"
                        required={false}
                        register={register}
                        type="number"
                        maxLength={24}
                        minLength={8}
                        rules={{
                            validate: (value) =>
                                validateMobile(value) || 'Min. 8 tegn',
                        }}
                    />
                    <FormMessage text="" error={errors?.telefon?.message} />
                </div>

                {options ? (
                    <div className={classes.selectContainer}>
                        <Select
                            placeholder={L('GENERISK_FORM_FOAAFDELING_LABEL')}
                            value={departmentNumber}
                            defaultValue={departmentNumber}
                            useControlled
                            {...register('afdelingsnummer')}
                            onChange={(e) =>
                                setDepartmentNumber(e.target.value)
                            }
                            options={options}
                        />
                    </div>
                ) : null}

                <Button
                    style={{ height: '46px' }}
                    buttonType="submit"
                    type="primary"
                    size="large"
                    disabled={!isValid}
                >
                    {L('ALLEMEDLEMMER_FORM_SØG_KNAP')}
                </Button>
            </form>
            <div className={classes.subArea}>
                <button
                    type="button"
                    className={classes.linkButton}
                    onClick={() => {
                        setResult([])
                        reset({
                            fuldeNavn: '',
                            foedselsesdato: '',
                            telefon: '',
                            afdelingsnummer: '',
                        })
                        setSelectedDate(undefined)
                        setDepartmentNumber('')
                        setError(undefined)
                    }}
                >
                    {L('ALLEMEDLEMMER_FILTER_NULSTIL_LABEL')}
                </button>
            </div>
            {error && error.hasError && !loading ? (
                <div className={`${classes.content} page-up`}>
                    <div className={classes.messageWrapper}>
                        <CardWithIconAndMessge
                            icon={
                                <div className={classes.circleIcon}>
                                    <PersonSearch
                                        color={colors.green500}
                                        width="32px"
                                    />
                                </div>
                            }
                            message={error.message}
                        />
                    </div>
                </div>
            ) : null}
            {!members.length && !error && !loading ? (
                <div className={`${classes.content} page-up`}>
                    <Typography align="center" size="S">
                        {L('ALLEMEDLEMMER_HJÆLPETEKST_01')}
                    </Typography>
                </div>
            ) : null}
            <div className={classes.members}>
                {loading ? <Spinner /> : null}
                {!loading && members && members.length ? (
                    <div className="page-up">
                        <CardList>
                            <CardHeader
                                gridTemplateColumns={gridTemplateColumns}
                            >
                                <CardHeaderCell />
                                <CardHeaderCell>
                                    <SortButton
                                        onClick={() => setSort('name')}
                                        sort={sortState}
                                        name="name"
                                    >
                                        {L(
                                            'MINEMEDLEMMER_TABEL_KOLONNETITEL_NAVN'
                                        )}
                                    </SortButton>
                                </CardHeaderCell>
                                <CardHeaderCell>
                                    <Typography size="XS" weight="bold">
                                        <SortButton
                                            onClick={() =>
                                                setSort('arbejdssted')
                                            }
                                            sort={sortState}
                                            name="arbejdssted"
                                        >
                                            {L(
                                                'MINEMEDLEMMER_TABEL_KOLONNETITEL_ARBEJDSSTED'
                                            )}
                                        </SortButton>
                                    </Typography>
                                </CardHeaderCell>
                                <CardHeaderCell>
                                    <SortButton
                                        onClick={() => setSort('department')}
                                        sort={sortState}
                                        name="department"
                                    >
                                        {L('GENERISK_FORM_FOAAFDELING_LABEL')}
                                    </SortButton>
                                </CardHeaderCell>
                                <CardHeaderCell>
                                    <SortButton
                                        onClick={() => setSort('isOwnMember')}
                                        sort={sortState}
                                        name="isOwnMember"
                                    >
                                        Status
                                    </SortButton>
                                </CardHeaderCell>
                            </CardHeader>
                            {members.map((member) => (
                                <>
                                    <CardMobile
                                        gridTemplateColumns="100%"
                                        url={
                                            !member.isOwnMember
                                                ? `/alle-foas-medlemmer/${member.memberId}`
                                                : undefined
                                        }
                                    >
                                        <CardCell>
                                            <Circle>
                                                <CircleText>
                                                    {getInitials(
                                                        member.fullName
                                                    )}
                                                </CircleText>
                                            </Circle>
                                        </CardCell>
                                        <CardContentMobile>
                                            <CardCell>
                                                <Typography
                                                    tag="h3"
                                                    size="M"
                                                    spacing=".2rem"
                                                    color="rgba(0, 0, 0, 0.9)"
                                                >
                                                    {member.fullName}
                                                </Typography>
                                                <Typography
                                                    size="XS"
                                                    spacing=".2rem"
                                                    color="rgba(0, 0, 0, 0.6)"
                                                >
                                                    {
                                                        member?.primaryWorkPlace
                                                            ?.name
                                                    }
                                                </Typography>
                                                <Typography
                                                    size="XXS"
                                                    spacing=".2rem"
                                                    color="rgba(0, 0, 0, 0.4)"
                                                >
                                                    {
                                                        member?.primaryWorkPlace
                                                            ?.name
                                                    }
                                                </Typography>
                                            </CardCell>
                                        </CardContentMobile>
                                    </CardMobile>
                                    <Card
                                        url={
                                            !member.isOwnMember
                                                ? `/alle-foas-medlemmer/${member.memberId}`
                                                : undefined
                                        }
                                        key={member.memberId}
                                        gridTemplateColumns={
                                            gridTemplateColumns
                                        }
                                    >
                                        <CardCell>
                                            <Circle>
                                                <CircleText>
                                                    {getInitials(
                                                        member.fullName
                                                    )}
                                                </CircleText>
                                            </Circle>
                                        </CardCell>
                                        <CardCell>
                                            <Typography size="XS">
                                                {member.fullName}
                                            </Typography>
                                        </CardCell>
                                        <CardCell>
                                            <Typography size="XS">
                                                {member?.primaryWorkPlace?.name}
                                            </Typography>
                                        </CardCell>
                                        <CardCell>
                                            {member.department ? (
                                                <Typography size="XS">
                                                    {member.department.name}
                                                </Typography>
                                            ) : null}
                                        </CardCell>
                                        <CardCell>
                                            {member.isOwnMember ? (
                                                <Badge>
                                                    {L(
                                                        'ALLEMEDLEMMER_ALLEREDE-MEDLEM_STATUS_BADGE'
                                                    )}
                                                </Badge>
                                            ) : null}
                                            {member.isAddedByUser &&
                                            !member.isOwnMember ? (
                                                <Badge>
                                                    {L(
                                                        'ALLEMEDLEMMER_ANMODET_TILFØJ_MEDLEM_STATUS_BADGE'
                                                    )}
                                                </Badge>
                                            ) : null}
                                        </CardCell>
                                    </Card>
                                </>
                            ))}
                        </CardList>
                    </div>
                ) : null}
                {members.length ? (
                    <div className={classes.numberOfMembers}>
                        <Typography size="XS">
                            Viser {members.length} medlemmer
                        </Typography>
                    </div>
                ) : null}
            </div>
        </div>
    )
}

export { AllMembers }
